import React, { useMemo } from 'react';
import { View, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import type { MessagesStackParamList } from '@/navigation/residents/mobile/MessagesStackNavigator';
import { CHAT_MOBILE_COLORS } from '@/constants/residents/mobile/chatColors';
import { useResidentConversation } from '@/hooks/residents/useMessages';
import { ResidentChatWindowMobile } from '@/components/residents/propertyChat/mobile/ResidentChatWindowMobile';
import { ChatConversationHeader } from '@/components/residents/chat/mobile';

type Route = RouteProp<MessagesStackParamList, 'Conversation'>;

/**
 * Mobile Conversation Detail Screen
 * Displays chat messages with modal-style header
 */
export const ConversationScreen: React.FC = () => {
  const navigation = useNavigation();
  const { params } = useRoute<Route>();
  const { conversation } = useResidentConversation(params.conversationId);
  const insets = useSafeAreaInsets();

  // Format creation date
  const formattedCreatedAt = useMemo(() => {
    if (!conversation?.createdAt) return undefined;
    const date = new Date(conversation.createdAt);
    const dateStr = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
    return `${dateStr} ${timeStr}`;
  }, [conversation?.createdAt]);

  // Handle close/back
  const handleClose = () => {
    navigation.goBack();
  };

  const subject = conversation?.subject || params.subject || 'Conversation';

  // Calculate keyboard offset for modal - use only safe area top inset
  // Don't use useHeaderHeight() as it can return incorrect values in nested navigation
  const keyboardOffset = Platform.OS === 'ios' ? insets.top : 0;

  return (
    <SafeAreaView style={styles.container} edges={Platform.OS === 'android' ? ['top', 'bottom'] : ['bottom']}>
      <KeyboardAvoidingView
        style={styles.keyboardView}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        keyboardVerticalOffset={keyboardOffset}
      >
        {/* Header with subject, date, and close button */}
        <ChatConversationHeader
          subject={subject}
          createdAt={formattedCreatedAt}
          onClose={handleClose}
        />

        {/* Chat window with messages and input */}
        <View style={styles.chatContainer}>
          <ResidentChatWindowMobile conversationId={params.conversationId} />
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: CHAT_MOBILE_COLORS.cardBackground,
  },
  keyboardView: {
    flex: 1,
  },
  chatContainer: {
    flex: 1,
  },
});

export default ConversationScreen;
